import { useDebounce } from "../../../hooks/use-debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { showToast } from "../../../components/custom/custom-toast";
import { isSuccess } from "../../../models/response/base-response";
import ServiceStatusConfirmModal from "../../service/components/service-status-confirm-modal";
import ServiceMapTable, { ServiceMapRow } from "../components/service-map-table";
import ServiceGroupModal, { ServiceGroupFormValues } from "../components/service-group-modal";
import ServiceItemModal, { ServiceItemFormValues } from "../components/service-item-modal";
import { Option } from "../components/linked-category-picker";
import ServiceMapService, { ServiceMapListFilters, ServiceMapStatusCode } from "../services/service-map-service";
import ServiceTypeService, { ServiceType, ServiceTypeStatusCode } from "../../service-type/services/service-type-service";
import ServiceService, { Service, ServiceStatusCode } from "../../service/services/service-service";
import { ALL_BRANCHES_VALUE } from "../../../hooks/use-catalog-branch-options";
import UnitService, { UNIT_STATUS } from "../../units/services/unit-service";
import ClothingCategoryService, { CLOTHING_CATEGORY_STATUS } from "../../clothing-categories/services/clothing-category-service";

/** Số dịch vụ tối đa tải cho ô chọn dịch vụ (API cho phép tối đa 500). */
const SERVICE_TYPE_OPTION_LIMIT = 500;

/** Thêm lựa chọn hiện tại vào danh sách nếu chưa có (vd nhóm / dịch vụ đang tạm ngưng). */
const withCurrent = (options: Option[], value: number, label: string): Option[] =>
  options.some((o) => Number(o.value) === Number(value)) ? options : [{ value, label }, ...options];

/**
 * Bảng giá: mỗi dịch vụ (1 dòng service_type_maps) là 1 hàng độc lập, nhóm dịch vụ chỉ là 1 cột.
 * Tạo nhóm và thêm dịch vụ là 2 modal riêng; mỗi dòng sửa / tạm ngưng độc lập.
 * Danh mục đang dùng chung mọi chi nhánh (branch_id = 0).
 */
export const ServiceMapIndexPage: React.FC = () => {
  const [rows, setRows] = useState<ServiceMapRow[]>([]);
  const [tab, setTab] = useState<"active" | "inactive">("active");
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [keysearch, setKeysearch] = useState("");
  const [groupFilter, setGroupFilter] = useState<number | "">("");
  const [activeRecords, setActiveRecords] = useState(0);
  const [inactiveRecords, setInactiveRecords] = useState(0);
  const debouncedSearch = useDebounce(keysearch, 700);

  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [editRow, setEditRow] = useState<ServiceMapRow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<{ row: ServiceMapRow; target: "active" | "inactive" } | null>(null);

  // Tạm cố định "Dùng chung" (branch_id = 0) — danh mục đang vận hành dùng chung cho mọi chi nhánh.
  const branchId = ALL_BRANCHES_VALUE;

  /** APIs */
  const { fetch: fetchMaps, loading: loadingList } = ServiceMapService.list();
  const { fetch: createMapping } = ServiceMapService.createMapping();
  const { fetch: updateMapping } = ServiceMapService.updateMapping();
  const { changeStatus: changeMapStatus, loading: changingStatus } = ServiceMapService.changeStatus();
  const { fetch: fetchServiceTypes } = ServiceTypeService.list();
  const { fetch: fetchGroups } = ServiceService.list();
  const { fetch: createGroup } = ServiceService.create();
  const { fetch: fetchUnits } = UnitService.list();
  const { fetch: fetchClothingCategories } = ClothingCategoryService.list();

  // Giữ hàm fetch trong ref để effect không chạy lại mỗi lần render
  const apiRef = useRef({ fetchMaps, fetchServiceTypes, fetchGroups, fetchUnits, fetchClothingCategories });
  useEffect(() => {
    apiRef.current = { fetchMaps, fetchServiceTypes, fetchGroups, fetchUnits, fetchClothingCategories };
  }, [fetchMaps, fetchServiceTypes, fetchGroups, fetchUnits, fetchClothingCategories]);

  /** Danh mục cho modal và bộ lọc */
  const [groupOptions, setGroupOptions] = useState<Option[]>([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState<Option[]>([]);
  const [unitOptions, setUnitOptions] = useState<Option[]>([]);
  const [clothingCategoryOptions, setClothingCategoryOptions] = useState<Option[]>([]);

  const toList = (raw: any): any[] => (Array.isArray(raw) ? raw : Array.isArray(raw?.list) ? raw.list : []);

  const loadGroups = useCallback(async () => {
    const res = await apiRef.current.fetchGroups({ status: ServiceStatusCode.ACTIVE, branch_id: branchId });
    if (res && isSuccess(res)) {
      setGroupOptions(toList(res.data).map((g: Service) => ({ value: g.id, label: g.name })));
    }
  }, [branchId]);

  const loadServiceTypes = useCallback(async () => {
    const res = await apiRef.current.fetchServiceTypes({
      status: ServiceTypeStatusCode.ACTIVE,
      branch_id: branchId,
      page: 1,
      limit: SERVICE_TYPE_OPTION_LIMIT,
    });
    if (res && isSuccess(res)) {
      setServiceTypeOptions(
        toList(res.data).map((t: ServiceType) => ({ value: t.id, label: t.code ? `${t.name} (${t.code})` : t.name }))
      );
    }
  }, [branchId]);

  /**
   * Đơn vị bán và loại đồ — tải một lần, đổi rất ít.
   * `allSettled`: hai lời gọi tới hai service khác nhau (product / order) — một bên lỗi không kéo bên kia.
   */
  useEffect(() => {
    void loadGroups();
    void loadServiceTypes();
    void (async () => {
      const [unitRes, catRes] = await Promise.allSettled([
        apiRef.current.fetchUnits({ status: UNIT_STATUS.ACTIVE, limit: 100 }),
        apiRef.current.fetchClothingCategories({ status: CLOTHING_CATEGORY_STATUS.ACTIVE }),
      ]);
      if (unitRes.status === "fulfilled" && unitRes.value && isSuccess(unitRes.value)) {
        setUnitOptions(toList(unitRes.value.data).map((u: any) => ({ value: u.id, label: u.name })));
      } else {
        console.error("[service-map] Không tải được danh sách đơn vị:", unitRes);
      }
      if (catRes.status === "fulfilled" && catRes.value && isSuccess(catRes.value)) {
        setClothingCategoryOptions(toList(catRes.value.data).map((c: any) => ({ value: c.id, label: c.name })));
      } else {
        console.error("[service-map] Không tải được danh sách loại đồ:", catRes);
      }
    })();
  }, [loadGroups, loadServiceTypes]);

  /** Tìm loại đồ cho ô "Loại đồ liên kết". Deps rỗng: đổi identity sẽ làm effect debounce trong picker chạy lại. */
  const searchClothingCategories = useCallback(async (keyword: string): Promise<Option[]> => {
    const res = await apiRef.current.fetchClothingCategories({ status: CLOTHING_CATEGORY_STATUS.ACTIVE, key_search: keyword });
    if (!res || !isSuccess(res)) throw new Error("Không tìm được loại đồ");
    return toList(res.data).map((c: any) => ({ value: c.id, label: c.name }));
  }, []);

  /** Danh sách bảng giá — mỗi dòng mapping là 1 hàng */
  const loadMaps = useCallback(async () => {
    const status = tab === "active" ? ServiceMapStatusCode.ACTIVE : ServiceMapStatusCode.INACTIVE;
    const params: ServiceMapListFilters = {
      status,
      branch_id: branchId,
      limit,
      page,
      key_search: debouncedSearch,
      ...(groupFilter !== "" ? { service_id: groupFilter } : {}),
    };
    try {
      const res = await apiRef.current.fetchMaps(params);
      if (!res || !isSuccess(res)) {
        showToast({ message: res?.message ?? "Không thể tải danh mục dịch vụ", type: "error" });
        setRows([]);
        return;
      }
      const list: any[] = (res.data as any)?.list ?? [];
      const total = (res.data as any)?.total_record ?? 0;
      if (tab === "active") setActiveRecords(total);
      else setInactiveRecords(total);

      setRows(
        list
          // Bỏ dòng mồ côi (nhóm / dịch vụ đã bị xoá)
          .filter((item) => item.service && item.serviceType)
          .map((item) => ({
            id: item.id,
            serviceId: item.service_id,
            branchId: item.branch_id,
            serviceName: item.service.name,
            serviceCode: item.service.code,
            imageUrl: item.service.image_url,
            serviceTypeId: item.service_type_id,
            serviceTypeName: item.serviceType.name,
            serviceTypeCode: item.serviceType.code,
            price: Number(item.price),
            statusCode: Number(item.status),
            unitId: item.unit_id,
            unitName: item.unit_name,
            weightPerUnit: item.weight_per_unit,
            kind: item.kind,
            linkedCategoryIds: item.linked_category_ids,
          }))
      );
    } catch (error) {
      showToast({ message: error instanceof Error ? error.message : "Không thể tải danh mục dịch vụ", type: "error" });
      setRows([]);
    }
  }, [branchId, tab, limit, page, debouncedSearch, groupFilter]);

  useEffect(() => {
    void loadMaps();
  }, [loadMaps]);

  // Đổi tab / bộ lọc → về trang 1
  useEffect(() => {
    setPage(1);
  }, [tab, groupFilter]);

  /** Tạo nhóm dịch vụ */
  const onCreateGroup = async (v: ServiceGroupFormValues): Promise<boolean> => {
    setSubmitting(true);
    try {
      const res = await createGroup({
        branch_id: branchId,
        code: v.code,
        name: v.name,
        description: v.description,
        status: ServiceStatusCode.ACTIVE,
      });
      if (res && isSuccess(res)) {
        showToast({ message: "Tạo nhóm dịch vụ thành công", type: "success" });
        await loadGroups();
        return true;
      }
      showToast({ message: res?.message ?? "Không thể tạo nhóm dịch vụ", type: "error" });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  /** Thêm 1 dịch vụ vào nhóm */
  const onCreateItem = async (v: ServiceItemFormValues): Promise<boolean> => {
    setSubmitting(true);
    try {
      const res = await createMapping({
        service_id: v.serviceId,
        service_type_id: v.serviceTypeId,
        price: v.price,
        unit_id: v.unitId,
        weight_per_unit: v.weightPerUnit,
        linked_category_ids: v.linkedCategoryIds,
      });
      if (res && isSuccess(res)) {
        showToast({ message: "Thêm dịch vụ thành công", type: "success" });
        void loadMaps();
        return true;
      }
      showToast({ message: res?.message ?? "Không thể thêm dịch vụ", type: "error" });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  /** Sửa 1 dòng (không đổi nhóm / dịch vụ) */
  const onEditItem = async (v: ServiceItemFormValues): Promise<boolean> => {
    if (!editRow) return false;
    setSubmitting(true);
    try {
      const res = await updateMapping(editRow.id, {
        price: v.price,
        unit_id: v.unitId,
        weight_per_unit: v.weightPerUnit,
        linked_category_ids: v.linkedCategoryIds,
      });
      if (res && isSuccess(res)) {
        showToast({ message: "Cập nhật dịch vụ thành công", type: "success" });
        void loadMaps();
        return true;
      }
      showToast({ message: res?.message ?? "Không thể cập nhật dịch vụ", type: "error" });
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // Quyền tạo / sửa / bật-tắt do BE kiểm tra (product service: @Privileges trên ServicesMapController) —
  // web không tự chặn nữa, thiếu quyền thì hiện thông báo lỗi BE trả về.
  const onEditOpen = (row: ServiceMapRow) => {
    setEditRow(row);
  };
  const onRequestDeactivate = (row: ServiceMapRow) => {
    setPendingStatus({ row, target: "inactive" });
  };
  const onRequestActivate = (row: ServiceMapRow) => {
    setPendingStatus({ row, target: "active" });
  };

  const onConfirmChangeStatus = async () => {
    if (!pendingStatus) return;
    const next = pendingStatus.target === "active" ? ServiceMapStatusCode.ACTIVE : ServiceMapStatusCode.INACTIVE;
    const res = await changeMapStatus(pendingStatus.row.id, { status: next });
    if (res && isSuccess(res)) {
      showToast({ message: pendingStatus.target === "active" ? "Đã kích hoạt" : "Đã tạm ngưng", type: "success" });
      void loadMaps();
    } else {
      showToast({ message: res?.message ?? "Không thể cập nhật trạng thái", type: "error" });
    }
    setPendingStatus(null);
  };

  const tabButton = (key: "active" | "inactive", label: string, count: number) => (
    <button
      onClick={() => setTab(key)}
      className={`relative flex items-center gap-2 pb-2 text-sm font-semibold uppercase tracking-wide transition ${
        tab === key ? "text-green-600" : "text-slate-400 hover:text-slate-600"
      }`}
    >
      <span>{label}</span>
      <span
        className={`${
          tab === key ? "bg-green-100 text-green-600" : "bg-slate-200 text-slate-500"
        } rounded-full px-2.5 py-0.5 text-xs font-semibold`}
      >
        {count}
      </span>
      {tab === key ? <span className="pointer-events-none absolute inset-x-0 -bottom-1 h-1 rounded-full bg-green-500" /> : null}
    </button>
  );

  return (
    <div className="">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-green-600 pl-3">Nhóm dịch vụ</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setGroupModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl border-2 border-green-600 bg-white px-5 py-3 text-sm font-bold text-green-700 shadow-sm transition hover:bg-green-50"
          >
            <span className="text-lg leading-none">+</span>
            <span>TẠO NHÓM DỊCH VỤ</span>
          </button>
          <button
            onClick={() => setItemModalOpen(true)}
            className="group inline-flex items-center gap-2 rounded-xl bg-green-600 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-green-200 transition-all hover:bg-green-700 hover:-translate-y-0.5"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 transition-transform group-hover:rotate-90">
              <path fillRule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clipRule="evenodd" />
            </svg>
            <span>THÊM DỊCH VỤ</span>
          </button>
        </div>
      </div>

      <div className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-100">
        <div className="flex flex-col gap-3 rounded-2xl bg-slate-100 px-6 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-8">
            {tabButton("active", "Đang hoạt động", activeRecords)}
            {tabButton("inactive", "Tạm ngưng", inactiveRecords)}
          </div>

          {/* Lọc theo nhóm dịch vụ */}
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-slate-600">Nhóm:</label>
            <select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value === "" ? "" : Number(e.target.value))}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">Tất cả nhóm</option>
              {groupOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6">
          <ServiceMapTable
            rows={rows}
            isLoading={loadingList}
            startIndex={(page - 1) * limit + 1}
            onEdit={onEditOpen}
            onDeactivate={tab === "active" ? onRequestDeactivate : undefined}
            onActivate={tab === "inactive" ? onRequestActivate : undefined}
            page={page}
            pageSize={limit}
            search={keysearch}
            setPage={setPage}
            setPageSize={setLimit}
            setSearch={setKeysearch}
            totalRecords={tab === "active" ? activeRecords : inactiveRecords}
          />
        </div>
      </div>

      {groupModalOpen && (
        <ServiceGroupModal onClose={() => setGroupModalOpen(false)} onSubmit={onCreateGroup} isSubmitting={submitting} />
      )}

      {itemModalOpen && (
        <ServiceItemModal
          mode="create"
          groupOptions={groupOptions}
          serviceTypeOptions={serviceTypeOptions}
          unitOptions={unitOptions}
          clothingCategoryOptions={clothingCategoryOptions}
          onSearchClothingCategories={searchClothingCategories}
          // Đang lọc theo 1 nhóm → chọn sẵn nhóm đó
          initialValues={groupFilter !== "" ? { serviceId: groupFilter } : undefined}
          onClose={() => setItemModalOpen(false)}
          onSubmit={onCreateItem}
          isSubmitting={submitting}
        />
      )}

      {editRow && (
        <ServiceItemModal
          mode="edit"
          // Nhóm / dịch vụ đang tạm ngưng không có trong danh sách "đang hoạt động" → thêm để modal vẫn hiện đúng tên
          groupOptions={withCurrent(groupOptions, editRow.serviceId, editRow.serviceName)}
          serviceTypeOptions={withCurrent(
            serviceTypeOptions,
            editRow.serviceTypeId,
            editRow.serviceTypeCode ? `${editRow.serviceTypeName} (${editRow.serviceTypeCode})` : editRow.serviceTypeName
          )}
          unitOptions={unitOptions}
          clothingCategoryOptions={clothingCategoryOptions}
          onSearchClothingCategories={searchClothingCategories}
          initialValues={{
            serviceId: editRow.serviceId,
            serviceTypeId: editRow.serviceTypeId,
            price: editRow.price,
            unitId: editRow.unitId,
            weightPerUnit: editRow.weightPerUnit,
            linkedCategoryIds: editRow.linkedCategoryIds ?? "",
          }}
          onClose={() => setEditRow(null)}
          onSubmit={onEditItem}
          isSubmitting={submitting}
        />
      )}

      {pendingStatus ? (
        <ServiceStatusConfirmModal
          mode={pendingStatus.target === "inactive" ? "deactivate" : "activate"}
          serviceName={`${pendingStatus.row.serviceName} • ${pendingStatus.row.serviceTypeName}`}
          onCancel={() => setPendingStatus(null)}
          onConfirm={onConfirmChangeStatus}
          isProcessing={changingStatus}
          onViewDetail={() => undefined}
        />
      ) : null}
    </div>
  );
};

export default ServiceMapIndexPage;
